import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogPrimitiveContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { ComponentProps, MouseEventHandler, ReactNode } from 'react'

type OmniDialogProps = ComponentProps<typeof Dialog> & {
  title?: string
  description?: string
  onClose?: MouseEventHandler<HTMLButtonElement>
  dialogTrigger?: ReactNode
  contentClassName?: string
  triggerClassName?: string
  bodyClassName?: string
  headerClassName?: string
  titleClassName?: string
  withCloseButton?: boolean
  clickOutsideToClose?: boolean
}

const styles = {
  overlay: `fixed inset-0 bg-[#000000B2]`,
  content: `bg-vive-surface-normal fixed top-1/2 left-1/2 z-[1000] flex max-h-[85dvh] w-[min(92dvw,640px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[7px] p-0 shadow-xl`,
  header: `bg-vive-surface-normal text-vive-white sticky top-0 z-10 flex flex-col items-center justify-center gap-y-2 px-6 pt-5 md:gap-y-4 md:px-10 md:pt-8`,
  title: `header2-omni-styles text-center`,
  close: `focus:ring-vive-primary-30 hover:stroke-vive-white active:stroke-vive-primary-30 absolute top-4 right-4 inline-flex size-7 items-center justify-center rounded-sm transition-colors outline-none focus:ring-2 md:top-5 md:right-5`,
  body: `dialog-scrollbar flex flex-col gap-y-6 overflow-y-auto px-6 pb-6 md:px-10 md:py-8`,
}

export default function OmniDialog({
  title = '',
  description = '',
  dialogTrigger,
  children,
  contentClassName,
  triggerClassName,
  bodyClassName,
  headerClassName,
  titleClassName,
  clickOutsideToClose = true,
  withCloseButton = true,
  onClose,
  ...props
}: OmniDialogProps) {
  return (
    <Dialog {...props}>
      {dialogTrigger && (
        <DialogTrigger className={cn('block size-max', triggerClassName)}>
          {dialogTrigger}
        </DialogTrigger>
      )}
      <DialogPortal>
        <DialogOverlay className={styles.overlay} />
        <DialogPrimitiveContent
          tabIndex={-1}
          className={cn(styles.content, contentClassName)}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            const target = e.currentTarget as HTMLElement
            target.focus()
          }}
          onInteractOutside={clickOutsideToClose ? undefined : (e) => e.preventDefault()}
          onEscapeKeyDown={clickOutsideToClose ? undefined : (e) => e.preventDefault()}
        >
          <div className={cn(styles.header, headerClassName)}>
            {withCloseButton && (
              <DialogClose className={styles.close} onClick={onClose} aria-label="close">
                <X className="size-7 transition-all" />
              </DialogClose>
            )}

            {title.trim().length > 0 && (
              <DialogTitle className={cn(styles.title, titleClassName)}>{title}</DialogTitle>
            )}

            {description.trim().length > 0 && (
              <p className="body1-omni-styles text-vive-primary-20">{description}</p>
            )}
          </div>
          {children && <div className={cn(styles.body, bodyClassName)}>{children}</div>}
        </DialogPrimitiveContent>
      </DialogPortal>
    </Dialog>
  )
}
